'use client'

import useAppsFlyer from '@/hooks/useAppsFlyer'
import { useT } from '@/phrase/hooks/useT'
import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import QRCode from 'react-qr-code'

interface DownloadQrModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  url?: string
  title?: string
  description?: string
  className?: string
}

const DownloadQrModal = ({
  open,
  onOpenChange,
  url,
  title,
  description,
  className = '',
}: DownloadQrModalProps) => {
  const t = useT()
  const { generatedUrl } = useAppsFlyer()
  const panelRef = useRef<HTMLDivElement | null>(null)

  const resolvedUrl = url || generatedUrl.clickURL
  const resolvedTitle = title || t('modals_popup_download_download')
  const resolvedDescription = description || t('modals_popup_download_desc')

  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false)
    }

    const onClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (panelRef.current && !panelRef.current.contains(target)) {
        onOpenChange(false)
      }
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open, onOpenChange])

  return (
    <div
      aria-hidden={!open}
      className={clsx(
        'absolute left-1/2 top-full z-[100] mt-3 -translate-x-1/2 transition-all duration-200 ease-out',
        open
          ? 'opacity-100 translate-y-0'
          : 'pointer-events-none opacity-0 -translate-y-1',
      )}
    >
      <div
        ref={panelRef}
        className={clsx(
          'inline-flex flex-col items-start p-2 rounded-3xl border border-white/100',
          className,
        )}
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.50)',
          backdropFilter: 'blur(5px)',
        }}
      >
        <div 
          className="flex items-center gap-3 p-3 rounded-2xl bg-white"
          style={{ height: '149px' }}
        >
          <div 
            className="flex justify-center items-center aspect-square bg-white p-2 rounded-lg border-2 border-[#10F48B]"
            style={{
              width: '121px',
              height: '121px',
            }}
          >
            {resolvedUrl && (
              <QRCode
                value={resolvedUrl}
                size={105}
                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
              />
            )}
          </div>
          <div 
            className="flex flex-col justify-center items-start self-stretch"
            style={{
              width: '167px',
              gap: '13px',
            }}
          >
            <p 
              className="uppercase"
              style={{
                color: '#1A1D25',
                fontFamily: '"Encode Sans Expanded"',
                fontSize: '16px',
                fontWeight: 900,
                lineHeight: '19px',
                letterSpacing: '0',
              }}
            >
              {resolvedTitle}
            </p>
            <p 
              className="self-stretch"
              style={{
                color: '#67697C',
                fontFamily: '"Encode Sans Expanded"',
                fontSize: '10px',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              {resolvedDescription}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DownloadQrModal
